/*
 * Authors: koenweyn, mariushirjanu
 * Created: 30/08/2019
 */
import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { IDescribedStringObject } from '@type_definitions/datatypes';
import * as _ from 'underscore';
import { util } from '@app/shared/util';
import { FormattingService } from '@app/shared/services/formatting.service';
import { IOrderPaymentDetails, PAYMENT_STATUS_OPEN, PAYMENT_STATUS_REFUSED, PAYMENT_TYPE_BANK, PAYMENT_TYPE_CREDITCARD, PAYMENT_TYPE_PAYPAL, PaymentDataService, POType } from './payment-data.service';
import { EVENT_CHANGE_BILLING_TYPE, EVENT_SAVE_ORDER, GA1Service } from '@app/shared/ga1/ga1.service';
import { GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { BILLINGTYPE_CENTRAL, BILLINGTYPE_LOCAL } from '@app/shared/services/billing/billing-data.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import { GaValidateService } from '@app/shared/formly/ga-validate.service';
import { PaymentFormattingService } from './payment-formatting.service';
import { IGAEvent } from '@app/shared/services/ga-event.service';
import { Subscription } from 'rxjs';
import { UntypedFormGroup } from '@angular/forms';
import { GaAlertComponent } from '@app/shared/alert/ga-alert.component';

interface IOrderPaymentModel {
  localPaymentTerms:string;
  centralPaymentTerms:string;
  paymentType:IDescribedStringObject;
  poNumber:string;
  paymentStatus:string;
  paymentTotal:number;
  paymentCurrency:string;
  prepaid:boolean;
}

interface IButtonsState {
  update?:boolean;
  CC?:boolean;
  PP?:boolean;
}

@Component({
  selector: 'order-payment',
  template: `
    <h2 *ngIf="loading">
      <activity-indicator toLoad="ORDERENTRY.PAYMENT.TITLE"></activity-indicator>
    </h2>
    <formly [form]="form" [fields]="fields" [model]="model" [noValidate]="true">
      <button class="btn btn-primary" (click)="updatePaymentType()" [busyButton]="buttonBusy.update" *ngIf="buttonVisible.update" [bbReplaceIcon]="true"><i class="fa fa-save margin-right-xs"></i><span translate>ORDERENTRY.PAYMENT.UPDATE_DETAILS</span></button>
      <button class="btn btn-success" (click)="payWithCreditCard()" [busyButton]="buttonBusy.CC" *ngIf="buttonVisible.CC" [bbReplaceIcon]="true"><i class="fa fa-credit-card margin-right-xs"></i><span translate>ORDERENTRY.PAYMENT.PAY_CC</span></button>
      <button class="btn btn-primary" (click)="payWithPayPal()" [busyButton]="buttonBusy.PP" *ngIf="buttonVisible.PP" [bbReplaceIcon]="true"><i class="fa fa-paypal margin-right-xs"></i><span translate>ORDERENTRY.PAYMENT.PAY_PP</span></button>
    </formly>
  `
})
export class OrderPaymentComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public order:number;

  public form:UntypedFormGroup = new UntypedFormGroup({});
  public fields:IExtraFieldConfigurationObject[];
  public loading:boolean;
  public buttonVisible:IButtonsState = {};
  public buttonBusy:IButtonsState = {};
  public model:IOrderPaymentModel;

  private ga1EventHandler:Subscription;
  private data:IOrderPaymentDetails;
  private paymentTypeEditableField:IExtraFieldConfigurationObject;
  private paymentTypeValueField:IExtraFieldConfigurationObject;
  private poNumberEditableField:IExtraFieldConfigurationObject;
  private localPaymentTermsField:IExtraFieldConfigurationObject;
  private centralPaymentTermsField:IExtraFieldConfigurationObject;


  constructor(
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private paymentFormattingService:PaymentFormattingService,
    private paymentDataService:PaymentDataService,
    private translationService:TranslationService,
    private validateService:GaValidateService,
    private ga1Service:GA1Service
  ) {
    /***/
  }

  public ngOnInit():void {
    this.loadPaymentDetails();
    this.listen();
  }

  private listen():void {
    this.ga1EventHandler = this.ga1Service.events.subscribe((event:IGAEvent) => {
      switch (event.name) {
        case EVENT_SAVE_ORDER:
          if (this.buttonVisible.update) {
            this.updatePaymentType(true);
          } else {
            this.paymentDataService.notifyPaymentSaved(); // send callback event to ga1 to continue saving the order
          }
          break;

        case EVENT_CHANGE_BILLING_TYPE: {
          const billingType = event.data?.billingType;
          this.localPaymentTermsField.hide = billingType === BILLINGTYPE_CENTRAL;
          this.centralPaymentTermsField.hide = billingType === BILLINGTYPE_LOCAL;
          if (this.data) {
            this.data.billingType = billingType;
          }
          break;
        }
      }
    });
  }

  private get isPaymentNotPaid():boolean {
    return (!this.model.paymentStatus || this.model.paymentStatus === PAYMENT_STATUS_OPEN || this.model.paymentStatus === PAYMENT_STATUS_REFUSED);
  }

  private get isOrderCanBePaid():boolean {
    return this.data.paymentStatus && this.data.minimalOrderStateReached && this.data.orderState !== 'CANCEL' && !this.data.testOrder;
  }

  private createFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aTitle('ORDERENTRY.PAYMENT.TERMS')
        .build(),
      this.localPaymentTermsField = this.formlyService.aValue('localPaymentTerms', 'ORDERENTRY.PAYMENT.TERMS')
        .withFormat(this.formattingService.formatPaymentTerms)
        .withHideExpression(() => !this.data || this.data?.billingType === BILLINGTYPE_CENTRAL)
        .build(),
      this.centralPaymentTermsField = this.formlyService.aValue('centralPaymentTerms', 'ORDERENTRY.PAYMENT.TERMS')
        .withFormat(this.formattingService.formatPaymentTerms)
        .withHideExpression(() => !this.data || this.data?.billingType === BILLINGTYPE_LOCAL)
        .build(),

      this.formlyService.aTitle('ORDERENTRY.PAYMENT.TITLE')
        .build(),
      this.formlyService.aCustom()
        .withComponent(GaAlertComponent)
        .withoutWrapper()
        .withExtraOptions({
          templateOptions: {
            inputs: {
              type: 'success',
              message: 'ORDERENTRY.PAYMENT.FULLY_PREPAID'
            }
          }
        })
        .withHideExpression(() => !this.model.prepaid)
        .build(),
      this.formlyService.aValue('paymentTotal', 'ORDERENTRY.PAYMENT.TOTAL')
        .withFormat(total => this.formattingService.formatAmount(total, this.model.paymentCurrency))
        .withHideExpression(() => !this.data.paymentTotal)
        .build(),
      this.formlyService.aValue('paymentStatus', 'ORDERENTRY.PAYMENT.STATUS')
        .withFormat(paymentStatus => this.paymentFormattingService.formatPaymentStatus(paymentStatus, this.model.paymentType && this.model.paymentType.id, this.data.paymentTotal, this.data.paymentCurrency, this.data.paymentDate))
        .withHideExpression(() => !this.data.paymentStatus)
        .build(),
      this.paymentTypeEditableField = this.formlyService.aRadio('paymentType', 'ORDERENTRY.PAYMENT.TYPE')
        .isRequired()
        .withFormat(this.formattingService.formatLov)
        .isHide()
        .withValueClass('col-sm-9 radio-payment-type')
        .withOptions(this.possiblePaymentTypes)
        .withExtraOptions({
          templateOptions: {
            inline: true
          }
        })
        .onChange(this.updateFormState)
        .build(),
      this.paymentTypeValueField = this.formlyService.aValue('paymentType', 'ORDERENTRY.PAYMENT.TYPE')
        .isRequired()
        .withFormat(this.formattingService.formatLov)
        .isHide()
        .build(),
      this.poNumberEditableField = this.formlyService.anInput('poNumber', 'ORDERENTRY.PAYMENT.PONR')
        .isRequired()
        .withValueClass('col-sm-3')
        .onChange(this.updateFormState)
        .withHideExpression(() => !_.isUndefined(this.data?.poNumber))
        .isHide()
        .build(),
      this.formlyService.aValue('poNumber', 'ORDERENTRY.PAYMENT.PONR')
        .isHide()
        .withHideExpression(() => this.isPaymentNotPaid && _.isUndefined(this.data?.poNumber))
        .build(),
      this.formlyService.aCustom()
        .withComponent(GaAlertComponent)
        .withoutWrapper()
        .withExtraOptions({
          templateOptions: {
            inputs: {
              message: 'ORDERENTRY.PAYMENT.PAYMENT_LINK_INFO',
              type: 'success'
            }
          }
        })
        .withHideExpression(() => this.model.paymentType?.id === PAYMENT_TYPE_BANK || (this.data.minimalOrderStateReached && !!this.data.paymentStatus))
        .build(),
      this.formlyService.aCustom()
        .withComponent(GaAlertComponent)
        .withoutWrapper()
        .withExtraOptions({
          templateOptions: {
            inputs: {
              message: 'ORDERENTRY.PAYMENT.ORDER_CANCELLED',
              type: 'warning'
            }
          }
        })
        .withHideExpression(() => this.data.orderState !== 'CANCEL')
        .build(),
      this.formlyService.aCustom()
        .withComponent(GaAlertComponent)
        .withoutWrapper()
        .withExtraOptions({
          templateOptions: {
            inputs: {
              message: 'ORDERENTRY.PAYMENT.TEST_ORDER',
              type: 'warning'
            }
          }
        })
        .withHideExpression(() => !this.data.testOrder)
        .build()
    ];
  }

  private formChanged():boolean {
    return this.model.paymentType?.id !== this.data.paymentType
      || this.model.poNumber !== this.data.poNumber;
  }

  private updateFormState = () => {
    if (!this.fields) {
      return;
    }

    this.buttonVisible.update = false;
    this.buttonVisible.CC = false;
    this.buttonVisible.PP = false;

    if (!this.formChanged()) {
      if (this.model.paymentType?.id === PAYMENT_TYPE_CREDITCARD && this.isOrderCanBePaid) {
        this.buttonVisible.CC = true;
      } else if (this.model.paymentType?.id === PAYMENT_TYPE_PAYPAL && this.isOrderCanBePaid) {
        this.buttonVisible.PP = true;
      }
    } else {
      this.buttonVisible.update = true;
    }

    //TODO KW
    this.localPaymentTermsField.hide = !this.model || !this.model.localPaymentTerms;
    this.centralPaymentTermsField.hide = !this.model || !this.model.centralPaymentTerms;

    // this means the credit card is not cleared or clearing yet -> customer can change payment type and try to pay
    if (this.model && this.isPaymentNotPaid) {
      this.paymentTypeEditableField.hide = false;
      this.paymentTypeValueField.hide = true;
    } else {
      this.paymentTypeEditableField.hide = true;
      this.paymentTypeValueField.hide = false;
      this.buttonVisible.CC = false;
      this.buttonVisible.PP = false;
    }

    this.poNumberEditableField.templateOptions.required = this.data.poType === POType.MANDATORY || (this.model?.paymentType?.id === PAYMENT_TYPE_BANK && this.data.poType === POType.MANUAL);
  };

  private loadPaymentDetails(refresh?:boolean):Promise<void> {
    if (!refresh) {
      this.loading = true;
    }
    return this.paymentDataService.loadOrderPaymentDetails(this.order)
      .then((paymentDetails:IOrderPaymentDetails) => {
        this.data = paymentDetails;
        this.model = {
          localPaymentTerms: paymentDetails.localPaymentTerms,
          centralPaymentTerms: paymentDetails.centralPaymentTerms,
          paymentType: paymentDetails.paymentType && {
            id: paymentDetails.paymentType,
            description: _.find(this.possiblePaymentTypes, pt => pt.id === paymentDetails.paymentType).description
          },
          poNumber: paymentDetails.poNumber,
          paymentStatus: paymentDetails.paymentStatus,
          paymentTotal: paymentDetails.paymentTotal,
          paymentCurrency: paymentDetails.paymentCurrency,
          prepaid: paymentDetails.prepaid
        };

        this.possiblePaymentTypes = _.filter(this.possiblePaymentTypes, pt => _.contains(paymentDetails.possiblePaymentTypes, pt.id) || pt.id === this.model.paymentType?.id);
      })
      .then(() => {
        if (!refresh) {
          this.fields = this.createFields();
        }

        this.updateFormState();
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public updatePaymentType(save?:boolean):void {
    // notify GA1 only when the update is coming by saving the order
    const notify:Function = (notifyFn:Function):void => {
      if (save) {
        notifyFn();
      }
    };

    this.validateService.validateFields(this.fields, this.model)
      .then(() => {
        this.buttonBusy.update = true;

        // sending ga1 message payment is currently being processed
        notify(this.paymentDataService.notifyPaymentSaving);

        const paymentTypeBackup:IDescribedStringObject = util.copyAndExtend(this.model.paymentType);
        const poNumberBackup:string = this.model.poNumber;

        this.paymentDataService.updateOrderPaymentDetails(this.order,
          {
            paymentType: this.model.paymentType.id,
            poNumber: this.model.poNumber,
            poType: this.data?.poType
          })
          .then(() => this.loadPaymentDetails(true)) // retrieve new information data model from server
          .then(() => {
            this.paymentDataService.notifyPaymentUpdate(paymentTypeBackup?.id, poNumberBackup);
            this.form.markAsPristine();
          })
          .then(() => notify(this.paymentDataService.notifyPaymentSaved))
          .catch(() => notify(this.paymentDataService.notifyPaymentSaveFailed))
          .finally(() => {
            this.buttonBusy.update = false;
          });
      })
      .catch(() => {
        notify(this.paymentDataService.notifyPaymentSaveFailed);
      });
  }

  public payWithCreditCard():void {
    this.buttonBusy.CC = true;
    this.paymentDataService.payWithStripe(this.order, undefined, this.redirectUrl)
      .finally(() => {
        this.buttonBusy.CC = false;
      });
  }

  public payWithPayPal():void {
    this.buttonBusy.PP = true;
    this.paymentDataService.payWithPayPal(this.order, undefined, this.redirectUrl)
      .finally(() => {
        this.buttonBusy.PP = false;
      });
  }

  private get redirectUrl():string {
    let url:string = window.top.location.href;
    if (url.indexOf('O_OrderView_GA1_Edit.jsp') >= 0 && url.indexOf('Payment') === -1) {
      //make sure we jump to the Payment tab when coming back
      url += encodeURIComponent('&showTab=Payment');
    }
    return url;
  }

  private possiblePaymentTypes:IDescribedStringObject[] = [
    {
      id: PAYMENT_TYPE_BANK,
      description: this.translationService.instant('ORDERENTRY.PAYMENT.BANK_TRANSFER')
    },
    {
      id: PAYMENT_TYPE_CREDITCARD,
      description: this.translationService.instant('ORDERENTRY.PAYMENT.CREDIT_CARD')
    },
    {
      id: PAYMENT_TYPE_PAYPAL,
      description: 'PayPal'
    }
  ];

  public ngOnChanges(changes:SimpleChanges):void {
    if (changes.order) {
      this.order = changes.order.currentValue;
      this.loadPaymentDetails();
    }
  }

  public ngOnDestroy():void {
    this.ga1EventHandler.unsubscribe();
  }
}